import React from 'react';
import { Trans } from 'react-i18next';
import { differenceInCalendarDays, isSameDay } from 'date-fns';

import { Claim } from '../../../api/types';
import Text from '../../common/Text/Text';
import { formatDate } from '../../parcel-finder/cards/dateHelper';
import { ClaimStatus } from '../../parcel-finder/claims/types';

import styles from './ClaimsTableSubmissionDate.scss';
import Tooltip from '../../common/Tooltip';
import Locale from '../../Translate';

export type ClaimsTableSubmissionDateProps = {
  claim: Pick<Claim, 'status' | 'submittedAt' | 'deadlineLastSubmissionDate' | 'id'>;
};

function ClaimsTableSubmissionDate({ claim }: ClaimsTableSubmissionDateProps) {
  const { status, submittedAt, deadlineLastSubmissionDate } = claim;

  if (!submittedAt && status === ClaimStatus.DECLINED) {
    return (
      <div data-tip data-for={`${claim.id}-submission-date-tooltip`}>
        <Text ink>
          <Locale>Not submitted</Locale>
        </Text>
        <Tooltip id={`${claim.id}-submission-date-tooltip`} position="top">
          <Trans i18nKey="CLAIM_DRAFT_NO_SUBMISSION_DATE_TOOLTIP" />
        </Tooltip>
      </div>
    );
  }

  if (!submittedAt && status === ClaimStatus.DRAFT) {
    if (!deadlineLastSubmissionDate) {
      return (
        <Text ink>
          <Trans i18nKey="CLAIMS_LIST_DUE_DEADLINE_TEXT" ink values={{ daysLeft: '?' }} />
        </Text>
      );
    }

    const now = new Date();
    const isDueToday = isSameDay(deadlineLastSubmissionDate, now);
    const daysUntilDeadline = differenceInCalendarDays(deadlineLastSubmissionDate, now);
    const isDueTomorrow = daysUntilDeadline === 1;

    if (isDueToday) {
      return (
        <span className={styles.warning}>
          <Trans i18nKey="CLAIMS_LIST_PAST_DEADLINE_DUE_TODAY_TEXT" />
        </span>
      );
    }

    if (isDueTomorrow) {
      return (
        <span className={styles.warning}>
          <Trans i18nKey="CLAIMS_LIST_PAST_DEADLINE_DUE_TOMORROW_TEXT" />
        </span>
      );
    }

    if (now > new Date(deadlineLastSubmissionDate)) {
      return (
        <span className={styles.error}>
          <Trans i18nKey="CLAIMS_LIST_PAST_DEADLINE_DUE_TEXT" />
        </span>
      );
    }

    return (
      <span className={daysUntilDeadline <= 3 ? styles.warning : styles.ink}>
        <Trans i18nKey={`CLAIMS_LIST_DUE_DEADLINE_TEXT`} values={{ daysLeft: daysUntilDeadline }} />
      </span>
    );
  }

  return <Text ink>{formatDate(submittedAt)}</Text>;
}

export default React.memo(ClaimsTableSubmissionDate);
